import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, TrendingUp, Store, PlusCircle, ImagePlus, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const emptyBiz = { name: "", category: "Food & Drink", city: "Aiken", address: "", short_tagline: "", website: "", phone: "", cover_image: "", photos: [] };

export default function Sponsor() {
  const { user } = useAuth();
  const [places, setPlaces] = useState([]);
  const [packages, setPackages] = useState([]);
  const [placeId, setPlaceId] = useState("");
  const [pkg, setPkg] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("existing"); // existing | new
  const [biz, setBiz] = useState(emptyBiz);
  const [uploading, setUploading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const selectedPkg = packages.find((p) => p.lookup_key === pkg);
  const recurring = !!selectedPkg?.recurring;

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      const url = `${BACKEND}${data.url}`;
      setBiz((b) => { const photos = [...(b.photos || []), url]; return { ...b, photos, cover_image: b.cover_image || photos[0] }; });
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeBizPhoto = (url) => setBiz((b) => { const photos = (b.photos || []).filter((x) => x !== url); return { ...b, photos, cover_image: photos[0] || "" }; });

  useEffect(() => {
    api.get("/places", { params: { limit: 500 } }).then((r) => setPlaces(r.data));
    api.get("/payments/packages").then((r) => setPackages(r.data));
  }, []);

  const startCheckout = async (id) => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: pkg, place_id: id, origin_url: window.location.origin,
      auto_renew: recurring, accept_terms: recurring ? acceptTerms : false,
    });
    window.location.href = data.checkout_url;
  };

  const checkout = async () => {
    if (!user) return toast.error("Sign in to purchase a placement");
    if (!pkg) return toast.error("Choose a package");
    if (recurring && !acceptTerms) return toast.error("Please accept the auto-renew terms to continue");
    setLoading(true);
    try {
      if (tab === "new") {
        if (!biz.name) { setLoading(false); return toast.error("Enter your business name"); }
        const { data: created } = await api.post("/submissions/place", biz);
        toast.success("Business added — completing your feature purchase…");
        await startCheckout(created.id);
      } else {
        if (!placeId) { setLoading(false); return toast.error("Choose a listing to feature"); }
        await startCheckout(placeId);
      }
    } catch {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-8 md:px-8" data-testid="sponsor-page">
      <div className="mb-8 max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-accent-foreground">
          <TrendingUp size={13} /> For Businesses
        </span>
        <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight md:text-5xl">Feature your business on CSRAGo</h1>
        <p className="mt-3 text-muted-foreground">
          Get a "Sponsored" badge, top placement in Featured feeds, and priority in Explore & the Home hero.
          Reach thousands of locals and visitors across the CSRA.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <div>
          <h2 className="mb-3 font-serif text-2xl font-semibold">1. Choose your listing</h2>

          {/* tabs */}
          <div className="mb-3 flex gap-2 rounded-full border border-border bg-card p-1">
            <button onClick={() => setTab("existing")} data-testid="sponsor-tab-existing"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "existing" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Store size={15} /> Pick existing
            </button>
            <button onClick={() => setTab("new")} data-testid="sponsor-tab-new"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "new" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <PlusCircle size={15} /> Add my business
            </button>
          </div>

          {tab === "existing" ? (
            <div className="max-h-[420px] space-y-2 overflow-y-auto rounded-2xl border border-border bg-card p-3">
              {places.map((p) => (
                <button key={p.id} onClick={() => setPlaceId(p.id)} data-testid={`sponsor-place-${p.slug}`}
                  className={`flex w-full items-center gap-3 rounded-xl border p-2 text-left transition-colors ${placeId === p.id ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"}`}>
                  <img src={p.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.category} · {p.city}</p>
                  </div>
                  {placeId === p.id && <Check size={18} className="text-primary" />}
                </button>
              ))}
            </div>
          ) : !user ? (
            <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="sponsor-new-gate">
              <p className="text-sm text-muted-foreground">You need an account to add a new business.</p>
              <Link to="/auth" className="mt-3 inline-block rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Sign in / Sign up</Link>
            </div>
          ) : (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-4" data-testid="sponsor-new-form">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Business name *</span>
                <input value={biz.name} data-testid="sponsor-biz-name" onChange={(e) => setBiz({ ...biz, name: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Category</span>
                <select value={biz.category} data-testid="sponsor-biz-category" onChange={(e) => setBiz({ ...biz, category: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-muted-foreground">City</span>
                  <input value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-muted-foreground">Website</span>
                  <input value={biz.website} onChange={(e) => setBiz({ ...biz, website: e.target.value })} placeholder="https://"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                </label>
              </div>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Short tagline</span>
                <input value={biz.short_tagline} onChange={(e) => setBiz({ ...biz, short_tagline: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              </label>
              <div>
                <span className="mb-1 block text-sm font-medium text-muted-foreground">Photos <span className="font-normal">(optional, up to 6 — first one is the cover)</span></span>
                <div className="flex flex-wrap gap-2">
                  {(biz.photos || []).map((ph, i) => (
                    <div key={ph} className="relative h-16 w-16">
                      <img src={ph} alt="" className="h-16 w-16 rounded-lg object-cover" />
                      {i === 0 && <span className="absolute left-0 top-0 rounded-br rounded-tl-lg bg-primary px-1 text-[9px] font-semibold text-primary-foreground">Cover</span>}
                      <button type="button" onClick={() => removeBizPhoto(ph)} data-testid={`sponsor-photo-remove-${i}`}
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  {(biz.photos || []).length < 6 && (
                    <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary">
                      {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="sponsor-biz-cover" />
                    </label>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">New businesses are reviewed by our team and go live (featured) once approved after payment.</p>
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-3 font-serif text-2xl font-semibold">2. Pick a package</h2>
          <div className="space-y-3">
            {packages.map((p) => (
              <button key={p.lookup_key} onClick={() => setPkg(p.lookup_key)} data-testid={`sponsor-package-${p.lookup_key}`}
                className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-all ${pkg === p.lookup_key ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:-translate-y-0.5"}`}>
                <div>
                  <p className="flex items-center gap-1.5 font-serif text-lg font-bold">
                    <Sparkles size={16} className="text-accent" /> {p.label}
                    {p.recurring && <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary">Auto-renew</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {p.recurring ? "Renews automatically every month until you cancel" : `${p.days} days of featured & sponsored placement`}
                  </p>
                </div>
                <span className="font-serif text-2xl font-bold text-primary">
                  ${p.amount}{p.recurring && <span className="text-xs font-medium text-muted-foreground">/mo</span>}
                </span>
              </button>
            ))}
          </div>
          {recurring && (
            <label className="mt-3 flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="sponsor-terms">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                data-testid="sponsor-terms-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
              <span>{selectedPkg?.terms || "Your placement renews automatically each period and your card is charged until you cancel. You can cancel anytime before the renewal date from your account. Charges already made are non-refundable, and partial periods are not refunded."}</span>
            </label>
          )}
          <button onClick={checkout} disabled={loading || (recurring && !acceptTerms)} data-testid="sponsor-checkout-btn"
            className="mt-4 w-full rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {loading ? "Redirecting to secure checkout…" : recurring ? "Continue to auto-renew checkout" : "Continue to payment"}
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Secure checkout by Stripe · Test mode (card 4242 4242 4242 4242)</p>
          {!user && <p className="mt-2 text-center text-xs"><Link to="/auth" className="font-semibold text-primary">Sign in</Link> to purchase.</p>}
        </div>
      </div>
    </div>
  );
}
